<!-- eslint-disable vuejs-accessibility/anchor-has-content -->
<template>
  <div
    :id="`${namespace}-photoswipe`"
    class="photoswipe-lightbox"
  >
    <template
      v-for="(mediaItem, index) of media"
      :key="index"
    >
      <button
        v-if="mediaItem.mediaType === 'video'"
        :ref="`${childRef}-${index}`"
        class="photoswipe-lightbox__trigger"
        :data-index="index"
        :data-pswp-src="formatSource(getVideoSource(mediaItem.sources).url)"
        data-pswp-type="video"
        data-my-size="1920x1080"
        :data-pswp-width="mediaItem.width || 1080"
        :data-pswp-height="mediaItem.height || 1920"
        type="button"
        :disabled="videoTriggerDisabled"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.photoswipe.open_video')"
        />

        <video-player
          ref="video"
          class="photoswipe-lightbox__video"
          :class="$class('hidden')"
          :display-overlay="false"
          is-autoplay
          :video="getVideoSource(mediaItem.sources)"
        />

        <video-player
          class="product-carousel__video"
          :display-overlay="false"
          is-autoplay
          :is-active="index === activeIndex"
          :video="getVideoSource(mediaItem.sources)"
          @video-control-hover="handleVideoControlHover"
        />
      </button>

      <button
        v-else
        :ref="`${childRef}-${index}`"
        class="photoswipe-lightbox__trigger"
        :data-index="index"
        :data-pswp-src="formatSource(mediaItem.image?.src) || formatSource(mediaItem.src)"
        :data-pswp-width="mediaItem.image?.width || mediaItem.width"
        :data-pswp-height="mediaItem.image?.height || mediaItem.height"
        type="button"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.photoswipe.open_image')"
        />

        <responsive-image
          :loading="index > 0 ? 'lazy' : null"
          :image="mediaItem.image || mediaItem"
          :alt="product.title + ' view ' + (index+1)"
        />
      </button>
    </template>
  </div>
</template>

<script>

/**
 * Vue: Photoswipe lightbox (photoswipe-lightbox)
 * -----------------------------------------------------------------------------
 * Component which initialises and renders a series of images which open into a
 * Lightbox from the image's origin.
 * - Modified from: https://photoswipe.com/vue-image-gallery/
 * - Cannot use any element apart from an anchor link to render the lightbox,
 *   so set the role to `presentation` to remove all semantics (mimics Splide).
 *
 * @param {Boolean} [activeIndex] - Optional index that matches with a carousel
 * index to autoplay videos.
 * @param {String} [childRef] - $refs string.
 * @param {Object} [config] - Additional settings for Photoswipe init.
 * @param {Array} media - Array of media items to render in Photobox.
 * @param {String} namespace - Unique namespace.
 * @param {String} title - Modal title.
 *
 */
import PhotoSwipeLightbox from 'photoswipe/lightbox'

import VideoPlayer from '~async/video-player/video-player'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'PhotoswipeLightbox',

  components: {
    ResponsiveImage,
    VideoPlayer,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: () => ({
        handle: '',
        images: [],
        media: [],
        title: '',
      }),
    },
    activeIndex: {
      type: [Boolean, Number],
      default: false,
    },
    childRef: {
      type: String,
      default: '',
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    media: {
      type: Array,
      default: () => [],
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
  },

  data() {
    return {
      lightbox: null,
      videoTriggerDisabled: false,
    }
  },

  watch: {

    /**
     * Focus active media's lightbox trigger button
     * on index change from thumbnail click
     */
    activeIndex(newIndex) {
      const refName = `${this.childRef}-${newIndex}`
      this.$refs[refName][0]?.focus()
    },
  },

  beforeMount() {
    this.localConfig = {
      arrowPrevSVG: '<svg class="icon icon__chevron icon__chevron--left-alt" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="none"><g clip-path="url(#a)"><path d="M10.535 15.494c-1.617-1.676-4.43-5.111-6.72-6.855.143-.395.455-.58.792-.859 1.718-1.406 3.46-2.778 5.196-4.16 1.187-.943 2.576-1.962 3.595-3.073-.176.059-.362.118-.538.177" stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.15"/></g><defs><clipPath id="a"><path d="M16 16H0V0h16z" fill="#fff"/></clipPath></defs></svg>',
      arrowNextSVG: '<svg class="icon icon__chevron icon__chevron--right-2" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="none"><g clip-path="url(#a)"><path d="M5.465.506c1.617 1.676 4.43 5.111 6.72 6.855-.143.395-.455.58-.792.859-1.718 1.406-3.46 2.778-5.196 4.16-1.187.943-2.576 1.962-3.595 3.073.176-.059.362-.118.539-.177" stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.15"/></g><defs><clipPath id="a"><path d="M0 0h16v16H0z" fill="#fff"/></clipPath></defs></svg>',
      closeSVG: '<svg class="icon icon__close" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g stroke="#000" stroke-linecap="round" stroke-linejoin="round" clip-path="url(#a)"><path d="M2.084 3.613C5.482 7.188 8.88 10.775 12.278 14.35a48.589 48.589 0 0 0 6.404 6.29c.872.607 1.427 1.314 2.488.518"/><path d="M22.687 2.463A101.327 101.327 0 0 0 7.63 13.225a15.201 15.201 0 0 1-2.311 4.118c-1.226 1.503-2.943 2.564-4.005 4.194"/></g><defs><clipPath id="a"><path d="M0 0v24h24V0z" fill="#fff"/></clipPath></defs></svg>',
      zoomSVG: false,
      bgOpacity: 1,
      gallery: `#${this.namespace}-photoswipe`,
      children: 'button.photoswipe-lightbox__trigger',
      thumbSelector: 'button',
      initialZoomLevel: 'fit',
      secondaryZoomLevel: 'fit',
      returnFocus: true,
      trapFocus: true,
      ...this.config,
    }
  },

  mounted() {
    this.lightboxInit()
  },

  unmounted() {
    this.destroyLightbox()
  },

  methods: {

    /**
     * Intitialise lightbox using Photoswipe API.
     */
    lightboxInit() {
      if (this.lightbox) {
        return
      }

      this.lightbox = new PhotoSwipeLightbox({
        ...this.localConfig,
        pswpModule: () => import('photoswipe'),
      })

      this.lightboxEventListeners()

      this.lightbox.init()
    },

    /**
     * Function to house event listeners for lightbox.
     */
    lightboxEventListeners() {
      this.lightbox.addFilter('itemData', (itemData) => {
        const videoUrl = itemData.element.dataset.pswpSrc

        if (!videoUrl) {
          return false
        }

        itemData.videoUrl = videoUrl
        return itemData
      })

      /**
       * Required to allow `<button>` elements to trigger opening the lightbox
       * with the appropriate image populated.
       */
      this.lightbox.addFilter('domItemData', (itemData) => {
        itemData.src = itemData.element.dataset.pswpSrc
        itemData.h = itemData.element.dataset.pswpHeight
        itemData.w = itemData.element.dataset.pswpWidth

        return itemData
      })

      /**
       * Add necessary ARIA attributes to root element.
       */
      this.lightbox.on('firstUpdate', () => {
        const rootElement = this.lightbox.pswp.element
        rootElement.setAttribute('role', 'dialog')
        rootElement.setAttribute('aria-modal', true)
        rootElement.setAttribute('aria-label', this.$string('accessibility.photoswipe.modal_label'))
      })

      this.lightbox.on('contentLoad', (event) => {
        const { content } = event

        if (this.media[content.index].mediaType !== 'video') {
          return
        }

        event.preventDefault()
        content.element = this.buildLightboxVideo()
      })
    },

    /**
     * Create div to hold video player component in lightbox.
     * @returns {Boolean|HTMlElement}
     */
    buildLightboxVideo() {
      const videoContainer = document.createElement('div')
      videoContainer.className = 'photoswipe-lightbox__video-container'

      const videoCopy = this.$refs.video.map((element) => element)

      if (!videoCopy) {
        return false
      }

      const videoCopyElement = videoCopy[0].$el

      videoCopyElement.classList.remove('is-hidden')
      videoContainer.appendChild(videoCopyElement)

      return videoContainer
    },

    /**
     * Destroy lightbox.
     */
    destroyLightbox() {
      if (!this.lightbox) {
        return
      }

      this.lightbox.destroy()
      this.lightbox = null
    },

    /**
     * Get video MP4 source object.
     * @param {Array} sources - Video sources.
     * @returns {Object}
     */
    getVideoSource(sources) {
      return sources?.find((source) => source.format === 'mp4')
    },

    /**
     * Format media source URL to ensure it begins with protocol
     * @param {String} url - Media source URL.
     */
    formatSource(url) {
      if (!url) {
        return ''
      }

      return url.match(/^https?/) ? url : `https:${url}`
    },

    /**
     * Handle video control hover events to disable lightbox trigger.
     * @param {Boolean} hovered - Whether the controls were hovered.
     */
    handleVideoControlHover(hovered) {
      this.videoTriggerDisabled = hovered
    },
  },
}
</script>

<style lang="scss">
@import './photoswipe-lightbox';
@import '@/components/photoswipe';
</style>
